/**
 * CheckButtonGroupUI - 单选按钮组组件
 * 提供可配置的单选按钮组，支持水平/垂直排列、图标+文字或仅图标模式
 */

import { getI18nText } from '@/utils/commonFunc';
import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface skyboxItem {
    value: string;
    title: string;
    titleI18nKey?: string;
    backgroundImage: string;
    i18nKey?: string;
    disabled?: boolean;
    className?: string;
    onClickEvent?: (item: skyboxItem, index: number) => void;
}

export interface SkyboxGroupOptions extends ElementOptions {
    /** 按钮列表 */
    items: skyboxItem[];
    /** 排列方向 */
    direction?: 'horizontal' | 'vertical';
    containerClass: '',
    /** 按钮组样式类 */
    groupClass?: string;
    /** 按钮样式类 */
    buttonClass?: string;
    /** 激活状态样式类 */
    activeClass?: string;
    /** 默认激活的按钮ID */
    defaultActiveId?: string;
    /** 标签文本 */
    label?: string;
    /** 是否显示标签 */
    showLabel?: boolean;

    /** 自定义标签样式类 */
    labelClass?: string;
    /** 多语言键 */
    labelI18nKey?: string;
}

export class SkyboxGroupUI{
    private ui: BootstrapUI;
    private container: HTMLElement;
    private options: SkyboxGroupOptions;
    private items: skyboxItem[] = [];
    private activeId: string | null = null;
    private buttons: Map<string, HTMLElement> = new Map();
    private onClickCallbacks: ((item: skyboxItem, index: number) => void)[] = [];
    private labelElement: HTMLElement | null = null;

    constructor(options: SkyboxGroupOptions) {
        this.ui = bootstrapUI;
        this.options = {
            direction: 'horizontal',
            groupClass: 'skybox-group',
            buttonClass: 'check-button',
            activeClass: 'active',
            showLabel: true,
            labelClass: 'skybox-group-label',
            ...options
        };

        this.items = this.options.items || [];
        this.container = this.createContainer();
        this.setupEventListeners();
        this.populateButtons();

        // 设置默认激活按钮
        if (this.options.defaultActiveId) {
            this.setActiveButton(this.options.defaultActiveId);
        }
    }

    private createContainer(): HTMLElement {
        let labeli18nKey = '';
        if (this.options.i18nKey) {
            labeli18nKey = this.options.i18nKey;
            this.options.i18nKey = '';
        }
        const container = this.ui.createElement({
            className: [
                'skybox-group-container',
                ...(this.options.containerClass ? [this.options.containerClass] : []),
                ...(Array.isArray(this.options.className) ? this.options.className : this.options.className ? [this.options.className] : [])
            ]
        });

        // 创建标签（如果启用）
        if (this.options.showLabel && this.options.label) {
            const label = this.ui.createElement({
                tag: 'label',
                className: [
                    this.options.labelClass || 'skybox-group-label'
                ],
                textContent: this.options.label,
                i18nKey: labeli18nKey
            });
            this.labelElement = label;
            container.appendChild(label);
        }

        // 创建按钮组容器
        const buttonGroupContainer = this.ui.createElement({
            className: [
                'skybox-group',
                ...(this.options.groupClass ? [this.options.groupClass] : []),
                this.options.direction === 'vertical' ? 'vertical' : 'horizontal'
            ]
        });

        container.appendChild(buttonGroupContainer);

        return container;
    }

    private setupEventListeners(): void {
        // 可以在这里添加全局事件监听器
    }

    private populateButtons(): void {
        // 找到按钮组容器
        const buttonGroupContainer = this.container.querySelector('.skybox-group') as HTMLElement;
        if (buttonGroupContainer) {
            buttonGroupContainer.innerHTML = '';
        }
        this.buttons.clear();

        this.items.forEach((item, index) => {
            const buttonElement = this.createButtonElement(item, index);
            this.buttons.set(item.value, buttonElement);
            if (buttonGroupContainer) {
                buttonGroupContainer.appendChild(buttonElement);
            }
        });
    }

    private createButtonElement(item: skyboxItem, index: number): HTMLElement {
        console.log('[button]item.titleI18nKey-----', item.titleI18nKey);
        const button = this.ui.createElement({
            tag: 'button',
            className: [
                'skybox-group-item',
                this.options.buttonClass || 'check-button',
                ...(this.options.direction === 'vertical' ? ['vertical'] : []),
                ...(item.className ? [item.className] : []),
                ...(item.disabled ? ['disabled'] : [])
            ],
            attributes: {
                type: 'button',
                'data-id': item.value,
                'data-index': index.toString(),
                'aria-pressed': 'false'
            },
            // i18nKey: item.i18nKey
        });

        // 添加标题（如果不是仅图标模式）
        if (item.title) {
            if (item.titleI18nKey) {
                button.setAttribute('title', getI18nText(item.titleI18nKey));
                button.setAttribute('data-i18n-title', item.titleI18nKey);
            }else{
                button.setAttribute('title', item.title);
            }
        }
if(item.backgroundImage){
    button.style.backgroundImage = `url(${item.backgroundImage})`;
}
        // 添加点击事件
        if (!item.disabled) {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                this.handleButtonClick(item, index);
            });
        }

        // 悬停效果现在由 CSS 处理

        return button;
    }

    private handleButtonClick(item: skyboxItem, index: number): void {
        if (this.activeId === item.value) {
            return;
        }

        // 执行按钮回调并获取返回值
        let shouldActivate = true;
        if (item.onClickEvent) {
            const result = item.onClickEvent(item, index);
            // 如果回调返回 false，则不激活按钮
            if (result !== undefined && result === false) {
                shouldActivate = false;
            }
        }

        // 执行全局回调
        this.onClickCallbacks.forEach(callback => callback(item, index));

        // 根据回调返回值决定是否设置当前按钮为激活状态
        if (shouldActivate) {
            this.setActiveButton(item.value);
        }
    }

    /**
     * 设置组件激活状态（启用/禁用整个组件）
     */
    public setActive(status: boolean): void {
        if (!status) {
            // 禁用整个组件
            this.container.classList.add('forbidden-event-dom');

            // 禁用所有按钮
            this.buttons.forEach((button, buttonId) => {
                this.setDisabled(buttonId, true);
            });
        } else {
            // 启用整个组件
            this.container.classList.remove('forbidden-event-dom');

            // 启用所有按钮
            this.buttons.forEach((button, buttonId) => {
                this.setDisabled(buttonId, false);
            });
        }
    }

    /**
     * 设置激活按钮
     */
    public setActiveButton(id: string): boolean {
        console.log('[setActiveButton]id-----', id);
        const item = this.items.find(item => item.value === id);
        if (!item || item.disabled) {
            alert('setActiveButton error');
            return false;
        }

        // 取消所有按钮的激活状态
        this.buttons.forEach((button, buttonId) => {
            this.deactivateButton(buttonId);
        });

        // 激活指定按钮
        this.activateButton(id);
        this.activeId = id;

        return true;
    }

    /**
     * 取消激活按钮
     */
    public deactivate(id: string): boolean {
        if (this.activeId === id) {
            this.deactivateButton(id);
            this.activeId = null;
            return true;
        }
        return false;
    }

    /**
     * 设置按钮禁用状态
     */
    public setDisabled(id: string, disabled: boolean): boolean {
        const item = this.items.find(item => item.value === id);
        const button = this.buttons.get(id);

        if (!item || !button) {
            return false;
        }

        item.disabled = disabled;

        if (disabled) {
            button.classList.add('disabled', 'opacity-50');
            button.style.cursor = 'not-allowed';
            button.setAttribute('disabled', 'true');

            // 如果当前激活的按钮被禁用，取消激活
            if (this.activeId === id) {
                this.activeId = null;
            }
        } else {
            button.classList.remove('disabled', 'opacity-50');
            button.style.cursor = 'pointer';
            button.removeAttribute('disabled');
        }

        return true;
    }

    private activateButton(id: string): void {
        const button = this.buttons.get(id);
        console.log('[activateButton]button-----', button);

        if (button) {
            button.classList.add(this.options.activeClass || 'active');
            button.setAttribute('aria-pressed', 'true');
        }
    }

    private deactivateButton(id: string): void {
        const button = this.buttons.get(id);
        if (button) {
            button.classList.remove(this.options.activeClass || 'active');
            button.setAttribute('aria-pressed', 'false');
        }
    }

    /**
     * 获取当前激活的按钮ID
     */
    public getActiveId(): string | null {
        return this.activeId;
    }

    /**
     * 获取当前激活的按钮项
     */
    public getActiveItem(): skyboxItem | null {
        return this.items.find(item => item.value === this.activeId) || null;
    }

    /**
     * 添加点击回调
     */
    public onButtonClick(callback: (item: skyboxItem, index: number) => void): void {
        this.onClickCallbacks.push(callback);
    }

    /**
     * 获取所有按钮项
     */
    public getItems(): skyboxItem[] {
        return [...this.items];
    }

    /**
     * 获取容器元素
     */
    public getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 获取标签文本
     */
    public getLabel(): string {
        return this.options.label || '';
    }

    /**
     * 设置标签文本
     */
    public setLabel(label: string): void {
        this.options.label = label;
        if (this.labelElement) {
            this.labelElement.textContent = label;
        } else if (label && this.options.showLabel) {
            // 如果标签元素不存在但需要设置标签，创建它
            const labelElement = this.ui.createElement({
                tag: 'label',
                className: [
                    this.options.labelClass || 'skybox-group-label'
                ],
                textContent: label
            });
            this.labelElement = labelElement;
            // 插入到按钮组容器之前
            const buttonGroupContainer = this.container.querySelector('.skybox-group');
            if (buttonGroupContainer) {
                this.container.insertBefore(labelElement, buttonGroupContainer);
            }
        }
    }

    /**
     * 设置是否显示标签
     */
    public setShowLabel(showLabel: boolean): void {
        this.options.showLabel = showLabel;
        if (showLabel && this.options.label) {
            if (!this.labelElement) {
                this.setLabel(this.options.label);
            }
        } else if (this.labelElement) {
            this.labelElement.remove();
            this.labelElement = null;
        }
    }

    /**
     * 销毁组件
     */
    public destroy(): void {
        this.onClickCallbacks = [];
        this.buttons.clear();
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}




